import { MoreHorizOutlined } from "@mui/icons-material";
import { Box, ButtonBase } from "@mui/material";
import { blue, blueGrey, grey } from "@mui/material/colors";
import { useMemo, useState } from "react";
import { useFileDocGroupAndTypesHook } from "../../hooks/useFileDocGroupAndTypesHook";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import SearchInput from "./SearchInput";
import SelectFileAssignmentInput from "./SelectFileAssignmentInput";

/**
 * DocTypeCategoryPicker
 *
 * Presents every docType the user can pick from the project's
 * `fileDocGroupAndTypes` registry, grouped by docGroup, as clickable chips.
 * Selecting a chip sets BOTH docGroup and docType in a single call — same
 * semantics as the legacy `SelectFileAssignmentInput` autocomplete.
 *
 * A final "Other" chip reveals the legacy autocomplete as an escape hatch
 * for edge-case types that don't fit the configured categories.
 *
 * Props mirror `SelectFileAssignmentInput`:
 *   - docGroup, docType, onChange({ docGroup, docType })
 */
const DocTypeCategoryPicker = ({
  docGroup,
  docType,
  onChange,
  disabled = false,
  allowedDocGroups,
  sx,
}) => {
  const { fileDocGroupAndTypes } = useFileDocGroupAndTypesHook();
  const [showOther, setShowOther] = useState(false);
  const [query, setQuery] = useState("");

  const hasAllowList =
    Array.isArray(allowedDocGroups) && allowedDocGroups.length > 0;

  const groups = useMemo(() => {
    const entries = Object.entries(fileDocGroupAndTypes || {})
      .map(([group, types]) => ({
        group,
        types: (Array.isArray(types) ? [...types] : []).sort((a, b) =>
          a.localeCompare(b),
        ),
      }))
      .filter((g) => g.types.length > 0);

    if (hasAllowList) {
      const allow = new Set(allowedDocGroups);
      return entries
        .filter((g) => allow.has(g.group))
        .sort((a, b) => a.group.localeCompare(b.group));
    }

    return entries.sort((a, b) => a.group.localeCompare(b.group));
  }, [fileDocGroupAndTypes, allowedDocGroups, hasAllowList]);

  // When the user types in the search box, narrow each group's chip list to
  // matches only and drop groups that have no matches. Matching against the
  // group name lets the user type e.g. "appraisal" to surface every chip in
  // that group; matching against the doc type name catches
  // individual types regardless of group.
  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map(({ group, types }) => {
        const groupMatches = group.toLowerCase().includes(q);
        if (groupMatches) return { group, types };
        const matchedTypes = types.filter((t) =>
          t.toLowerCase().includes(q),
        );
        return matchedTypes.length > 0 ? { group, types: matchedTypes } : null;
      })
      .filter(Boolean);
  }, [groups, query]);

  const hasSelection = Boolean(docGroup && docType);
  // Once anything is picked (or the user opened "Other"), collapse the chip
  // grid and show only the autocomplete. Clearing the autocomplete restores
  // chips automatically (docGroup/docType go back to null).
  const showInputOnly = hasSelection || showOther;

  // When no allow-list is passed, the categorized chip UI adds no value —
  // render the full-list autocomplete directly.
  if (!hasAllowList) {
    return (
      <SelectFileAssignmentInput
        docGroup={docGroup}
        docType={docType}
        onChange={(params) => {
          onChange?.({
            docGroup: params?.docGroup || null,
            docType: params?.docType || null,
          });
        }}
        fullWidth
        onlyDisplayDocType
        placeholder="Select document type"
      />
    );
  }

  const handleSelectChip = (group, type) => {
    if (disabled) return;
    const alreadySelected = docGroup === group && docType === type;
    if (alreadySelected) {
      onChange?.({ docGroup: null, docType: null });
      return;
    }
    onChange?.({ docGroup: group, docType: type });
    setShowOther(false);
  };

  const handleToggleOther = () => {
    if (disabled) return;
    setShowOther((prev) => !prev);
  };

  const handleInputChange = (params) => {
    const nextGroup = params?.docGroup || null;
    const nextType = params?.docType || null;
    onChange?.({ docGroup: nextGroup, docType: nextType });
    // Cleared from the autocomplete → drop back to the chip view.
    if (!nextGroup && !nextType) setShowOther(false);
  };

  return (
    <Box
      sx={{
        //
        // borderTop: `1px solid ${grey[100]}`,
        // mt: 0.3,
        ...sx,
      }}
    >
      {showInputOnly ? (
        <Box sx={{ mt: 2 }}>
          <SelectFileAssignmentInput
            docGroup={docGroup}
            docType={docType}
            onChange={handleInputChange}
            fullWidth
            onlyDisplayDocType
            placeholder="Search all document types"
          />
        </Box>
      ) : (
        <Flx column g={3} sx={{ mt: 2 }}>
          <Box>
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder="Search document types…"
              size="small"
              fullWidth
            />
          </Box>
          {filteredGroups.length === 0 ? (
            <Txt sx={{ fontSize: 12, color: grey[600], fontStyle: "italic" }}>
              No document types match "{query}".
            </Txt>
          ) : (
            filteredGroups.map(({ group, types }) => {
              const groupIsSelected = docGroup === group;
              return (
                <Box key={group}>
                  <DocTypeLabel>{group}</DocTypeLabel>
                  <Flx sx={{ flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
                    {types.map((type) => {
                      const isSelected = groupIsSelected && docType === type;
                      return (
                        <ChipButton
                          key={`${group}:${type}`}
                          isSelected={isSelected}
                          disabled={disabled}
                          sx
                          onClick={() => handleSelectChip(group, type)}
                        >
                          {type}
                        </ChipButton>
                      );
                    })}
                  </Flx>
                </Box>
              );
            })
          )}

          <Flx column>
            <DocTypeLabel>Other</DocTypeLabel>
            <Box>
              <ChipButton
                isSelected={false}
                disabled={disabled}
                onClick={handleToggleOther}
                icon={<MoreHorizOutlined sx={{ fontSize: 14 }} />}
              >
                Select from full list of document types
              </ChipButton>
            </Box>
          </Flx>
        </Flx>
      )}
    </Box>
  );
};

const DocTypeLabel = ({ children }) => {
  return (
    <Txt
      sx={{
        fontSize: 10.5,
        fontWeight: 500,
        // fontWeight: 700,
        color: blueGrey[700],
        fontStyle: "italic",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        mb: 0.5,
      }}
    >
      {children}
    </Txt>
  );
};

const ChipButton = ({ children, isSelected, disabled, onClick, icon }) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    disableRipple
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.25,
      py: 0.5,
      borderRadius: "16px",
      border: `1px solid ${isSelected ? blue[400] : grey[300]}`,
      bgcolor: isSelected ? blue[50] : "#fff",
      color: isSelected ? blue[800] : grey[600],
      fontSize: 10.5,
      fontWeight: isSelected ? 600 : 500,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
      transition: "all 0.12s ease",
      "&:hover": {
        borderColor: disabled ? grey[300] : isSelected ? blue[500] : grey[400],
        bgcolor: disabled ? "#fff" : isSelected ? blue[100] : grey[50],
      },
    }}
  >
    {icon}
    <span>{children}</span>
  </ButtonBase>
);

export default DocTypeCategoryPicker;
